import { useEffect, useState } from 'react';
import useGroupsContext from '@contexts/GroupsContext/hook';
import { DiscoveryStatusByProduct } from '@interfaces/discoveryStatus';
import {
  DiscoveryFeature,
  shouldShowDiscoveryCard,
} from '@utils/shouldShowDiscoveryCard';
import {
  dismissDiscoveryCard,
  getDiscoveryCardDismissal,
} from '@repository/discoveryCardDismissal';
import { findDiscoveryStatus } from '@api/discoveryStatus';
import useFeaturePermissionsContext from '@contexts/FeaturePermissionsContext/hook';
import { useFeatureToggleAccess } from '@hooks/useFeatureToggleAccess';
import { useSession } from 'next-auth/react';
import { FeatureToggleName } from '@interfaces/featureToggle';
import { FeatureName } from '@interfaces/featurePermission';

type Args = {
  /** Chave do status e da dispensa. */
  feature: DiscoveryFeature;
  /**
   * Nome do toggle e da permissão, **separados**: eles coincidem em
   * `integrations` e `remarketing` e divergem no downsell (`coupons`) e no
   * upsell (`offers`). Derivar um do outro esconde o card sem erro nenhum.
   */
  toggle: FeatureToggleName;
  /**
   * A permissão por produto do destino, quando existe.
   *
   * Opcional porque nem toda feature tem uma: o "indique e ganhe" é da CONTA, mora em `/referVib` e
   * não aparece em `FeatureName` — a própria página se protege só por toggle. Cobrar uma permissão
   * inexistente esconderia o convite por uma regra que não existe.
   */
  permission?: FeatureName;
  /**
   * A ação que o CTA vai executar no destino, e por isso a que precisa ser
   * checada aqui:
   *
   * - `view`: o destino é uma listagem e o convite é só olhar;
   * - `create`: a ação anunciada é configurar. Vale tanto para CTA que abre
   *   formulário de cadastro (o `useScreenAccess` do destino fecha com /404 sem
   *   `canCreate`) quanto para CTA que abre listagem onde o "Adicionar" é o
   *   caminho.
   *
   * Checar sempre `canView` convidaria quem só tem leitura para uma ação que a tela
   * de destino não oferece a ele.
   *
   * **Por que não `canCreate || canUpdate`:** com `upsell: false` o status diz que
   * não existe configuração ATIVA, e não distingue "nada configurado" de "existe uma
   * desativada". Aceitar `canUpdate` sozinho convidaria quem não pode criar para uma
   * lista vazia sem botão — convite quebrado, que é pior do que um caminho válido a
   * menos. Hoje a distinção é teórica: em `platform_feature_permission`, `create` e
   * `update` têm o mesmo bitmask em todas as features. Se a API algum dia disser que
   * existe configuração inativa, aceitar `canUpdate` passa a ser correto.
   */
  action?: 'view' | 'create' | 'update';
  selectedIds: number[];
  hasSales: boolean;
};

const DIA_EM_MS = 24 * 60 * 60 * 1000;

type ScheduleEvidence = {
  /** Contatos somados sobre as comunidades de que o card fala. */
  contacts: number;
  /** Dias desde o disparo mais antigo do conjunto; `null` se alguma nunca disparou. */
  daysSinceLastMessage: number | null;
  /** As comunidades atrasadas — o CTA leva estas, e o plural sai do tamanho daqui. */
  ids: number[];
};

type Evidence = {
  touches: number;
  revenue: number;
  /** Compradores dos últimos 90 dias, somados sobre as comunidades de que o card fala. */
  buyers: number;
  /** Compradores de uma compra só, na comunidade que o CTA abre. */
  singlePurchaseBuyers: number;
  /**
   * Compras sem extra oferecido, SOMADAS sobre as comunidades de que o card fala.
   *
   * Esta soma, ao contrário das duas de cima, porque é evento e não pessoa: duas compras em duas
   * comunidades são duas chances perdidas, enquanto a mesma pessoa em duas comunidades é uma pessoa.
   */
  purchasesWithoutBump: number;
};

type Result = {
  show: boolean;
  /**
   * O card ainda não sabe se quer aparecer: o toggle está carregando, ou o status ainda não é o do
   * recorte atual.
   *
   * Existe para o desempate entre cards. Cada `useDiscoveryCard` busca o próprio toggle, e eles
   * resolvem em tempos diferentes — sem distinguir "carregando" de "recusado", um convite de
   * prioridade baixa apareceria primeiro só porque o de cima ainda não respondeu, e seria trocado
   * quando ele respondesse. No meio disso o criador pode ler, ou dispensar, o convite errado.
   */
  isPending: boolean;
  productCode: string | undefined;
  /**
   * O nome da comunidade que o CTA abre.
   *
   * Existe para a cópia poder DIZER de quem é o número. O card da afiliação conta compradores de uma
   * comunidade só — porque comprador é pessoa e pessoa não soma entre comunidades —, e sem nomeá-la o
   * texto "N pessoas compraram de você" seria lido como o total do recorte.
   */
  productName: string | undefined;
  dismiss: () => void;
  /** A audiência parada da mensagem agendada. Vazia para as outras features. */
  scheduleEvidence: ScheduleEvidence;
  /**
   * A fatia que o dono recebe das vendas de quem ele indicar.
   *
   * Vem do status porque é do `platform_user`, e não da comunidade: repete igual em todos os produtos
   * dele. Zero para as outras features, que não a usam.
   */
  referralRate: number;
  evidence: Evidence;
};

/**
 * Mais de uma tela monta duas instâncias de `useDiscoveryCard` com o mesmo recorte — o relatório de
 * Vendas soma uma para `integrations` e outra para `upsell`, cada uma com seu próprio efeito de busca.
 * Sem isto, um filtro aplicado uma vez dispara duas requisições HTTP idênticas de `discoveryStatus`.
 *
 * Mesmo padrão de `api/featureToggle.ts#isToggleActive`: deduplicação de chamada em voo, não cache de
 * resultado — a entrada some assim que a promise resolve ou rejeita, então a busca seguinte (recorte
 * novo, ou nova leitura depois de configurar algo) nunca serve uma resposta velha.
 */
const inFlightStatusRequests = new Map<
  string,
  Promise<DiscoveryStatusByProduct>
>();

const findDiscoveryStatusDeduped = (
  ids: number[],
): Promise<DiscoveryStatusByProduct> => {
  const key = ids.join(',');

  const inFlight = inFlightStatusRequests.get(key);
  if (inFlight) return inFlight;

  const request = findDiscoveryStatus(ids).finally(() => {
    inFlightStatusRequests.delete(key);
  });

  inFlightStatusRequests.set(key, request);
  return request;
};

/**
 * Plumbing shared by every report screen that offers the discovery card: read the dismissal, fetch the
 * feature status for the selected communities (discarding stale responses), and pick which of them is the
 * card's entry point.
 *
 * `hasSales` stays a caller input on purpose — whether the current recorte has sales to justify the card is
 * something only the page's own report data can answer.
 */
export const useDiscoveryCard = (args: Args): Result => {
  const { getAllProducts, getUserRoleByProduct } = useGroupsContext();
  const { getUserPermissions } = useFeaturePermissionsContext();
  const { data: session } = useSession();
  const [status, setStatus] = useState<DiscoveryStatusByProduct>({});
  /*
    A que recorte o `status` pertence.

    Sem isto, um filtro que descarta id ainda sem status faria o card falar de um
    SUBCONJUNTO em silêncio: ao passar de [A] para [A, B], o hook segura o status
    antigo enquanto a busca nova está em voo, B não tem status, o filtro o descarta e
    o `every` do `shouldShowDiscoveryCard` — que é quem garante falhar fechado —
    nunca vê o id que faltava. O card apareceria com total parcial, e ficaria parcial
    para sempre se a busca nova falhasse.
  */
  const [statusIds, setStatusIds] = useState<string | null>(null);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);

  const { isFeatureEnabled, isFeatureLoading } = useFeatureToggleAccess({
    name: args.toggle,
    valuesToValidate: { platformUserId: Number(session?.user?.id) },
  });

  useEffect(() => {
    setDismissedAt(getDiscoveryCardDismissal(args.feature));
  }, [args.feature]);

  useEffect(() => {
    if (!args.selectedIds.length) return;

    // Descarta uma resposta antiga que chega depois de uma seleção mais nova.
    let ignore = false;

    findDiscoveryStatusDeduped(args.selectedIds)
      .then((result) => {
        if (ignore) return;

        setStatus(result);
        setStatusIds(args.selectedIds.join(','));
      })
      .catch(console.error);

    return () => {
      ignore = true;
    };
    // `join` evita refazer a busca quando o array é recriado com os mesmos ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [args.selectedIds.join(',')]);

  // A porta de entrada do card é a primeira comunidade do recorte que o usuário pode de fato configurar —
  // um afiliado enxergando o relatório de uma comunidade não pode agir sobre ela. Percorrer todo o recorte
  // (em vez de olhar só a primeira posição) evita esconder o card quando a primeira comunidade selecionada
  // é uma afiliação, mas uma comunidade depois dela é acionável.
  // Poder agir não é só uma questão de papel. A feature ainda pode estar desligada por toggle para este
  // usuário, ou fora da permissão dele neste produto — e as duas coisas já barram o destino do CTA: o menu
  // do produto esconde a entrada, e a página de remarketing redireciona para /404 com o toggle desligado.
  // Enquanto o toggle carrega, tratamos como indisponível, para não piscar um convite que pode não valer.
  const isFeatureAvailable = !isFeatureLoading && isFeatureEnabled;

  const temCodigo = (id: number) =>
    getAllProducts().find((product) => product.id === id)?.productCode;

  const actionableIds = !isFeatureAvailable
    ? []
    : args.selectedIds.filter((id) => {
        const role = getUserRoleByProduct(id);
        if (role !== 'OWNER' && role !== 'COPRODUCER') return false;

        // Sem permissão declarada não há o que checar por produto — o papel acima já filtrou.
        if (!args.permission) return Boolean(temCodigo(id));

        const permissions = getUserPermissions(args.permission, id);
        // Escrever pressupõe ler, e é o que `useScreenAccess` cobra no destino:
        // `isAllowed = canView && permissions[action]`. Cobrar só `canCreate` aqui
        // deixaria passar a combinação inversa — cria mas não vê — e o CTA cairia
        // em /404 do mesmo jeito.
        if (!permissions.canView) return false;

        if (args.action === 'create' && !permissions.canCreate) return false;

        // `update` quando o destino EDITA uma configuração que já existe, em vez de cadastrar uma. É o
        // caso da afiliação: a linha de `affiliation_config` existe para 99,7% das comunidades e nasce
        // `OFF`, então ligar o programa é uma edição — e a tela de destino mostra o botão de salvar só
        // com `canUpdate`, sem olhar `canCreate` nenhuma vez.
        if (args.action === 'update' && !permissions.canUpdate) return false;

        return Boolean(temCodigo(id));
      });

  /*
    Para o upsell, o destino não pode ser qualquer comunidade acionável: o card fala
    de toques que aconteceram, e abrir a tela de uma comunidade sem toque nenhum
    entrega um número que não é dela. A evidência é somada sobre as mesmas
    comunidades de que o card fala — e comunidade sem toque somaria zero de todo
    jeito, então o total não muda: muda de quem o card está falando.
  */
  const isStatusReady = statusIds === args.selectedIds.join(',');

  /*
    Para a mensagem agendada o card fala das comunidades ATRASADAS e com audiência:

    - `scheduleMessages === false` é a régua de cadência, calculada na API. Sem filtrar,
      uma única comunidade em dia derrubaria o convite das outras, porque o
      `shouldShowDiscoveryCard` exige `every(... === false)`.
    - `botContacts > 0` porque sem contato não há a quem enviar — e deixar a vazia
      dentro contaminaria a data de pior caso, fazendo o card dizer "nunca" sobre uma
      audiência que é toda de outra comunidade.
    - `active` porque o `InputProduct` da tela de envio usa `onlyActiveGroups`: id de
      comunidade inativa vai para uma tela que não consegue exibi-lo.
  */
  const cardIds =
    args.feature === 'upsell'
      ? actionableIds.filter((id) => (status[id]?.upsellTouches ?? 0) > 0)
      : args.feature === 'scheduleMessages'
      ? actionableIds.filter(
          (id) =>
            status[id]?.scheduleMessages === false &&
            (status[id]?.botContacts ?? 0) > 0 &&
            getAllProducts().find((product) => product.id === id)?.active,
        )
      : /*
          A afiliação fala de quem pode divulgar, e quem divulga é quem já comprou: comunidade sem
          comprador na janela não tem público a recrutar, e o convite viraria a propaganda genérica
          que a régua de 8% do `backupStep` já mediu.
        */
      args.feature === 'affiliation'
      ? /*
           Este filtro é também a porta de "tem número próprio": sem comunidade com comprador não
           sobra `cardIds`, e sem `cardIds` não há `productCode` — o card some por aqui, e não por uma
           segunda checagem em `hasEvidence`. Duas guardas para o mesmo fato dariam um mutante que
           nenhum teste mata.
        */
        actionableIds.filter((id) => (status[id]?.buyers ?? 0) > 0)
      : /*
          A indicação é do DONO da comunidade, e é dele que o status fala. Para um coprodutor, o
          `referral` que chega é o do dono — e o CTA levaria o coprodutor para o `/referVib` DELE, que
          é outra conta. Sem este corte, o convite decidiria por um fato de outra pessoa.
        */
      /*
        O cupom fala de quem comprou uma vez só, então comunidade sem esse público não tem convite —
        mesma régua do número próprio que vale para o upsell e a afiliação.
      */
      /*
        O extra fala de compras que aconteceram sem nada oferecido junto. Sem compra assim não há
        chance perdida a mostrar, e o convite viraria propaganda genérica.
      */
      args.feature === 'bump'
      ? actionableIds.filter(
          (id) =>
            (status[id]?.purchasesWithoutBump ?? 0) > 0 &&
            // ...e onde dê para montar o par: o formulário do destino exige duas ofertas diferentes.
            status[id]?.bumpPairAvailable === true,
        )
      : args.feature === 'coupon'
      ? actionableIds.filter(
          (id) => (status[id]?.singlePurchaseBuyers ?? 0) > 0,
        )
      : args.feature === 'referral'
      ? actionableIds.filter((id) => getUserRoleByProduct(id) === 'OWNER')
      : actionableIds;

  const targetId = cardIds[0];
  const targetProduct = targetId
    ? getAllProducts().find((product) => product.id === targetId)
    : undefined;
  const productCode = targetProduct?.productCode;
  const productName = targetProduct?.name;

  /**
   * A evidência do upsell, somada **só sobre as comunidades acionáveis**.
   *
   * O card do upsell fala do que já aconteceu, então sem toque ele não tem o que
   * dizer — e um convite sem número próprio é a propaganda genérica que a régua de
   * 8% do `backupStep` já mediu. Zero esconde o card.
   *
   * Somar o recorte inteiro atribuiria o número errado: o relatório aceita
   * afiliações no filtro, e o CTA leva à primeira comunidade acionável. Toque que
   * é de uma afiliação faria o card aparecer falando de uma comunidade que não
   * teve toque nenhum.
   */
  const evidence = cardIds.reduce<Evidence>(
    (soma, id) => ({
      touches: soma.touches + (status[id]?.upsellTouches ?? 0),
      revenue: soma.revenue + (status[id]?.upsellRevenue ?? 0),
      buyers: soma.buyers,
      singlePurchaseBuyers: soma.singlePurchaseBuyers,
      purchasesWithoutBump:
        soma.purchasesWithoutBump + (status[id]?.purchasesWithoutBump ?? 0),
    }),
    /*
      Toque e receita SOMAM; comprador NÃO.

      A diferença é entre evento e pessoa. Dois toques em duas comunidades são dois toques, mas a mesma
      pessoa comprando em duas comunidades é uma pessoa — e `invoice.user_id` aponta para `user`, que é
      por comunidade, então ela aparece como dois compradores distintos. Medido: das 221.936 pessoas que
      compraram em 90 dias, 18,4% compraram em mais de uma comunidade, e somar por comunidade daria
      290.100 — 31% a mais de "pessoas" do que existem.

      Por isso o número dos compradores é o da comunidade que o CTA abre, e não a soma do recorte: é o
      único total que é verdade sem uma contagem distinta entre comunidades, que custaria juntar `user`
      (60 milhões de linhas) a cada abertura da home.
    */
    {
      touches: 0,
      revenue: 0,
      buyers: status[targetId]?.buyers ?? 0,
      // Pessoa não soma entre comunidades: ver a nota acima. Vale igual para este.
      singlePurchaseBuyers: status[targetId]?.singlePurchaseBuyers ?? 0,
      // Zero, porque este SOMA no reduce: compra é evento, e evento soma entre comunidades.
      purchasesWithoutBump: 0,
    },
  );

  /**
   * A audiência parada, somada só sobre as comunidades de que o card fala.
   *
   * `daysSinceLastMessage` é o PIOR caso do conjunto, não a média: com uma comunidade
   * há 47 dias e outra há 10, o card fala de 47. Média esconderia a mais parada atrás
   * da menos parada. `null` quando alguma nunca disparou — e só entram aqui
   * comunidades com contato, então "nunca" nunca vem de uma base vazia.
   */
  const scheduleEvidence = cardIds.reduce<ScheduleEvidence>(
    (soma, id) => {
      const ultimo = status[id]?.lastScheduledMessageAt ?? null;
      const dias =
        ultimo === null
          ? null
          : Math.floor((Date.now() - new Date(ultimo).getTime()) / DIA_EM_MS);

      return {
        contacts: soma.contacts + (status[id]?.botContacts ?? 0),
        daysSinceLastMessage:
          soma.daysSinceLastMessage === null || dias === null
            ? null
            : Math.max(soma.daysSinceLastMessage, dias),
        ids: [...soma.ids, id],
      };
    },
    { contacts: 0, daysSinceLastMessage: 0, ids: [] },
  );

  const hasEvidence =
    args.feature === 'upsell'
      ? evidence.touches > 0
      : args.feature === 'scheduleMessages'
      ? scheduleEvidence.contacts > 0
      : true;

  const show =
    Boolean(productCode) &&
    isStatusReady &&
    hasEvidence &&
    shouldShowDiscoveryCard({
      feature: args.feature,
      statusByProduct: status,
      // O mesmo escopo da evidência, e pelo mesmo motivo: comunidade de que o card
      // não fala — afiliação, ou acionável sem toque — não pode calar o convite da
      // comunidade que o CTA abre.
      selectedIds: cardIds,
      hasSales: args.hasSales,
      dismissedAt,
      now: Date.now(),
    });

  const dismiss = () => {
    dismissDiscoveryCard(args.feature);
    setDismissedAt(Date.now());
  };

  /*
    Sem recorte não há pendência: `isStatusReady` é falso enquanto não há seleção nenhuma, e tratar isso
    como "carregando" travaria os cards de menor prioridade para sempre em vez de por um instante.
  */
  const isPending =
    args.selectedIds.length > 0 && (isFeatureLoading || !isStatusReady);

  const referralRate = status[targetId]?.referralRate ?? 0;

  return {
    show,
    isPending,
    referralRate,
    productCode,
    productName,
    dismiss,
    evidence,
    scheduleEvidence,
  };
};
